import { Modal, useModals } from 'almanac-ui/Modal'

import Styles from './DowngradeLimitCheckModal.module.scss'
import React, { useEffect } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { ArrowUpRightSquareContained, Brand, Facebook, Instagram, Knowledge, LinkedIn, Twitter } from 'blaze-ui/icons'
import { BRAND_KITS_PATH, MEMBERS_PATH } from 'paths'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'
import { Button } from 'blaze-ui/Button'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import { useTierUsageLimits } from 'hooks/useTierUsageLimits'
import { pluralize } from 'utils'
import UserProfile1 from 'blaze-ui/icons/20/UserProfile1'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface Props {
  downgradePlanTier: PaymentPlanKey
}

export const DowngradeLimitCheckModal = observer(({ downgradePlanTier }: Props) => {
  const { closeModal, closeAllModals, openModal } = useModals()
  const history = useHistory()
  const workspace = useCurrentWorkspace()
  const { limitOverage: seatsOverage } = useTierUsageLimits({ limitKey: 'seats', overrideTier: downgradePlanTier })
  const { limitOverage: brandKitOverage } = useTierUsageLimits({
    limitKey: 'brand_kits',
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: sourceMaterialOverage } = useTierUsageLimits({
    limitKey: 'source_materials',
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: linkedInOverage } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: ExternalIntegrationEnum.LinkedIn,
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: instagramOverage } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: ExternalIntegrationEnum.Instagram,
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: facebookOverage } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: ExternalIntegrationEnum.Facebook,
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: twitterOverage } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: ExternalIntegrationEnum.X,
    overrideTier: downgradePlanTier,
  })
  const { limitOverage: wordpressOverage } = useTierUsageLimits({
    limitKey: 'integrations',
    integrationType: ExternalIntegrationEnum.WordPress,
    overrideTier: downgradePlanTier,
  })
  const tierUsageStore = useRootStore().tierUsageStore
  const { initialized, loading } = tierUsageStore

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  if (!workspace || loading) return null

  return (
    <Modal.Root onClose={closeModal} onPressOutside={closeModal}>
      <Modal.Header className={Styles.header}>
        <AlertTriangle size={32} /> <h3>Remove overages to downgrade</h3>
      </Modal.Header>
      <Modal.Content>
        <p className={Styles.label}>Below is the list of items that need to be adjusted to downgrade:</p>
        {!!seatsOverage && (
          <OverageRow
            icon={<UserProfile1 size={20} />}
            actionText="Members"
            onActionClick={() => {
              history.push(generatePath(MEMBERS_PATH, { workspaceId: workspace.id }))
            }}
          >
            Remove <span>{seatsOverage}</span> {pluralize('member', seatsOverage)} from your workspace
          </OverageRow>
        )}
        {!!brandKitOverage && (
          <OverageRow
            icon={<Brand size={20} />}
            actionText="Brand Kits"
            onActionClick={() => {
              history.push(generatePath(BRAND_KITS_PATH, { workspaceId: workspace.id }))
              closeAllModals()
            }}
          >
            Delete <span>{brandKitOverage}</span> Brand {pluralize('Kit', brandKitOverage)}
          </OverageRow>
        )}
        {!!sourceMaterialOverage && (
          <OverageRow
            icon={<Knowledge size={20} />}
            actionText="Source Materials"
            onActionClick={() => {
              history.push(generatePath(BRAND_KITS_PATH, { workspaceId: workspace.id }))
              closeAllModals()
            }}
          >
            Delete <span>{sourceMaterialOverage}</span> Source {pluralize('Material', sourceMaterialOverage)}
          </OverageRow>
        )}
        {!!instagramOverage && (
          <OverageRow
            icon={<Instagram size={20} />}
            actionText="Integrations"
            onActionClick={() => {
              closeAllModals()
              openModal(IntegrationsModal)
            }}
          >
            Disconnect <span>{instagramOverage}</span> Instagram {pluralize('Account', instagramOverage)}
          </OverageRow>
        )}
        {!!facebookOverage && (
          <OverageRow
            icon={<Facebook size={20} />}
            actionText="Integrations"
            onActionClick={() => {
              closeAllModals()
              openModal(IntegrationsModal)
            }}
          >
            Disconnect <span>{facebookOverage}</span> Facebook {pluralize('Account', facebookOverage)}
          </OverageRow>
        )}
        {!!twitterOverage && (
          <OverageRow
            icon={<Twitter size={20} />}
            actionText="Integrations"
            onActionClick={() => {
              closeAllModals()
              openModal(IntegrationsModal)
            }}
          >
            Disconnect <span>{twitterOverage}</span> X {pluralize('Account', twitterOverage)}
          </OverageRow>
        )}
        {!!wordpressOverage && (
          <OverageRow
            icon={<WordPressLogo size={20} />}
            actionText="Integrations"
            onActionClick={() => {
              closeAllModals()
              openModal(IntegrationsModal)
            }}
          >
            Disconnect <span>{wordpressOverage}</span> WordPress {pluralize('Account', wordpressOverage)}
          </OverageRow>
        )}
        {!!linkedInOverage && (
          <OverageRow
            icon={<LinkedIn size={20} />}
            actionText="Integrations"
            onActionClick={() => {
              closeAllModals()
              openModal(IntegrationsModal)
            }}
          >
            Disconnect <span>{linkedInOverage}</span> LinkedIn {pluralize('Account', linkedInOverage)}
          </OverageRow>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={closeModal}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {
            // The below button is always disabled because if they are seeing this modal, they have overages
          }
          <Button variant="primary" isDisabled={true} size="lg">
            Downgrade
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
})

interface OverageRowProps {
  icon: React.ReactNode
  onActionClick: () => void
  actionText: string
  children?: React.ReactNode
}

const OverageRow = ({ icon, actionText, children, onActionClick }: OverageRowProps) => {
  return (
    <div className={Styles.rowContainer}>
      <div className={Styles.rowLeftContainer}>
        {icon}
        <div>{children}</div>
      </div>
      <button className={Styles.link} onClick={onActionClick}>
        {actionText} <ArrowUpRightSquareContained size={20} />
      </button>
    </div>
  )
}
